import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Microphone,
  BookOpen,
  GraduationCap,
  Question,
  PaperPlaneTilt,
  EnvelopeSimple,
  MapPin,
  Headphones,
  ArrowUpRight,
  Sparkle,
  Heart,
  Quotes,
  Info,
  ChalkboardTeacher,
} from "@phosphor-icons/react";
import { API_BASE, IMAGES, SITE } from "../lib/site";
import { AUTHOR, BOOKS } from "../data/books";
import Seo from "../components/Seo";

const featuredBook = BOOKS.find((b) => b.id === "from-diagnosis-to-goodbye");

// Placeholder podcast episodes — replace each entry with the real episode
// title, summary, and listen URL when episodes go live.
const EPISODES = [
  {
    number: "01",
    title: "When Grief Doesn't Make Sense",
    summary:
      "A practical, faith-based conversation on what to do when grief arrives without warning — and how to find quiet ground while you wait for the path forward.",
    duration: "Coming soon",
    listen_url: null,
  },
  {
    number: "02",
    title: "Caregiving Without Losing Yourself",
    summary:
      "Honest encouragement for spouses, adult children, and family caregivers who feel quietly invisible while caring for someone they love.",
    duration: "Coming soon",
    listen_url: null,
  },
  {
    number: "03",
    title: "Faith on the Hard Days",
    summary:
      "What to do when prayer feels dry, scripture feels distant, and you're not sure how to talk to God about what you're carrying.",
    duration: "Coming soon",
    listen_url: null,
  },
];

const Pill = ({ children, accent = "primary" }) => (
  <span
    className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold ${
      accent === "secondary" ? "text-secondary" : "text-primary"
    }`}
  >
    <span
      className={`w-8 h-px ${accent === "secondary" ? "bg-secondary" : "bg-secondary"}`}
    />
    {children}
  </span>
);

// ---------- Sections ----------

const HeroSection = () => (
  <section className="relative overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-br from-softblue/40 via-white to-secondary/10 pointer-events-none" />
    <div className="container-x relative py-20 sm:py-28 grid lg:grid-cols-[1.15fr,0.85fr] gap-12 lg:gap-16 items-center">
      <div>
        <Pill>A faith-based podcast & teaching platform</Pill>
        <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] text-ink-900">
          Real Questions.
          <br />
          Honest Answers.
          <br />
          <span className="text-primary">Practical Hope.</span>
        </h1>
        <p className="mt-6 text-lg text-ink-700 leading-relaxed max-w-xl">
          Ask Chaplain Derrick is a faith-based podcast, book, and teaching platform offering
          practical encouragement for grief, faith, relationships, caregiving, emotional stress,
          and life's difficult seasons.
        </p>

        <div className="mt-10 flex flex-wrap gap-3">
          <a
            href="#podcast"
            data-testid="acd-cta-podcast"
            className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-6 py-3 font-semibold text-sm hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
          >
            <Microphone size={16} weight="duotone" />
            Listen to the Podcast
          </a>
          {featuredBook?.amazon_url && (
            <a
              href={featuredBook.amazon_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="acd-cta-book"
              className="inline-flex items-center gap-2 bg-secondary text-ink-900 rounded-full px-6 py-3 font-semibold text-sm hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
            >
              <BookOpen size={16} weight="duotone" />
              Buy the Book on Amazon
              <ArrowUpRight size={14} weight="bold" />
            </a>
          )}
          <Link
            to="/classes"
            data-testid="acd-cta-class"
            className="inline-flex items-center gap-2 border-2 border-primary text-primary rounded-full px-6 py-3 font-semibold text-sm hover:bg-primary/5 transition-all duration-300"
          >
            <GraduationCap size={16} weight="duotone" />
            Join a Class
          </Link>
          <a
            href="#submit-question"
            data-testid="acd-cta-question"
            className="inline-flex items-center gap-2 border-2 border-ink-200 text-ink-800 rounded-full px-6 py-3 font-semibold text-sm hover:border-primary hover:text-primary transition-all duration-300"
          >
            <Question size={16} weight="duotone" />
            Submit a Question
          </a>
        </div>
      </div>

      <div className="relative">
        <div className="absolute -inset-6 bg-gradient-to-br from-secondary/30 via-primary/10 to-transparent rounded-3xl blur-2xl" aria-hidden="true" />
        <div className="relative rounded-3xl overflow-hidden border border-ink-100 shadow-[0_25px_70px_-25px_rgba(15,23,42,0.25)]">
          <img
            src={AUTHOR.photo}
            alt="Chaplain Derrick — host of Ask Chaplain Derrick"
            className="w-full h-[420px] sm:h-[500px] object-cover"
            style={{ objectPosition: "center 30%" }}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-ink-900/85 via-ink-900/40 to-transparent p-6">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-semibold text-secondary">
                <span className="w-6 h-px bg-secondary" />
                Hosted by
              </div>
              <div className="mt-2 font-heading text-2xl leading-tight">Chaplain Derrick</div>
              <div className="text-white/80 text-sm mt-1">
                Author · Educator · Spiritual Care Companion
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const AboutSection = () => (
  <section id="about" className="py-20 sm:py-28 border-t border-ink-100">
    <div className="container-x grid lg:grid-cols-[1fr,1.2fr] gap-12 lg:gap-20 items-start">
      <div>
        <Pill accent="secondary">About the platform</Pill>
        <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-ink-900 leading-[1.1]">
          Honest encouragement for the questions you actually carry.
        </h2>
      </div>
      <div className="space-y-5 text-ink-700 text-base sm:text-lg leading-relaxed">
        <p>
          Ask Chaplain Derrick is a chaplain-led media platform built around one simple idea:
          most of us are quietly carrying questions about faith, grief, caregiving, and stress
          that we don't quite know who to ask.
        </p>
        <p>
          Through the podcast, books, classes, and a question-submission inbox, Chaplain
          Derrick offers warm, biblically grounded, professionally informed answers — the kind
          a trusted chaplain would offer over coffee, not from a stage.
        </p>
        <div className="flex flex-wrap gap-3 pt-2">
          {["Grief", "Caregiving", "Faith doubts", "Workplace stress", "End-of-life questions", "Spiritual care"].map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1.5 text-xs text-ink-700 bg-softblue/70 rounded-full px-3 py-1.5"
            >
              <Sparkle size={12} weight="duotone" className="text-secondary" />
              {t}
            </span>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const EpisodeCard = ({ ep }) => (
  <article
    className="bg-white rounded-2xl border border-ink-100 p-7 shadow-[0_10px_30px_-15px_rgba(15,23,42,0.12)] hover:-translate-y-1 hover:shadow-[0_20px_50px_-15px_rgba(15,23,42,0.18)] transition-all duration-300 flex flex-col"
    data-testid={`acd-episode-${ep.number}`}
  >
    <div className="flex items-start justify-between">
      <div className="font-heading text-5xl text-primary/30 leading-none">{ep.number}</div>
      <Headphones size={22} weight="duotone" className="text-secondary" />
    </div>
    <h3 className="mt-5 font-heading text-xl text-ink-900 leading-snug font-medium">
      {ep.title}
    </h3>
    <p className="mt-3 text-ink-700 text-sm leading-relaxed flex-1">{ep.summary}</p>
    <div className="mt-6 flex items-center justify-between">
      <span className="text-[11px] uppercase tracking-[0.18em] text-ink-500 font-semibold">
        {ep.duration}
      </span>
      {ep.listen_url ? (
        <a
          href={ep.listen_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-primary font-semibold hover:gap-2 transition-all"
        >
          Listen <ArrowUpRight size={14} weight="bold" />
        </a>
      ) : (
        <span className="text-xs text-ink-500">New episode dropping soon</span>
      )}
    </div>
  </article>
);

const PodcastSection = () => (
  <section id="podcast" className="py-20 sm:py-28 bg-softblue/30">
    <div className="container-x">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <div className="max-w-2xl">
          <Pill>Podcast episodes</Pill>
          <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-ink-900 leading-[1.1]">
            Quiet conversations. Practical hope.
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed">
            Short, intentional episodes you can listen to on a walk, in the carpool line, or in
            the quiet of the morning. New episodes go live on Apple Podcasts, Spotify, and
            wherever you listen.
          </p>
        </div>
        <a
          href="#submit-question"
          className="self-start inline-flex items-center gap-2 text-sm font-semibold text-primary hover:gap-3 transition-all"
        >
          Submit a question for an upcoming episode <ArrowUpRight size={14} weight="bold" />
        </a>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
        {EPISODES.map((ep) => (
          <EpisodeCard key={ep.number} ep={ep} />
        ))}
      </div>
    </div>
  </section>
);

const FeaturedBookSection = () => {
  if (!featuredBook) return null;
  return (
    <section id="book" className="py-20 sm:py-28">
      <div className="container-x grid lg:grid-cols-[auto,1fr] gap-12 lg:gap-20 items-center">
        <div className="mx-auto lg:mx-0">
          <div className="relative">
            <div className="absolute -inset-5 bg-gradient-to-br from-secondary/30 via-primary/10 to-transparent rounded-2xl blur-2xl" aria-hidden="true" />
            <img
              src={featuredBook.cover_image}
              alt={`${featuredBook.title} book cover`}
              className="relative w-56 sm:w-64 lg:w-72 rounded-xl shadow-2xl border border-ink-100"
            />
          </div>
        </div>
        <div>
          <Pill accent="secondary">Featured book</Pill>
          <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-ink-900 leading-[1.1]">
            {featuredBook.title}
          </h2>
          <p className="mt-3 font-heading text-lg sm:text-xl text-primary leading-snug">
            {featuredBook.subtitle}
          </p>
          <p className="mt-5 text-ink-700 leading-relaxed text-base sm:text-lg max-w-2xl">
            {featuredBook.description}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={featuredBook.amazon_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="acd-featured-book-cta"
              className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-6 py-3 font-semibold text-sm hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
            >
              Buy on Amazon <ArrowUpRight size={14} weight="bold" />
            </a>
            <Link
              to="/books"
              className="inline-flex items-center gap-2 border-2 border-primary text-primary rounded-full px-6 py-3 font-semibold text-sm hover:bg-primary/5 transition-all duration-300"
            >
              Explore more books
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const ClassesSection = () => (
  <section id="classes" className="py-20 sm:py-28 bg-softblue/30">
    <div className="container-x grid lg:grid-cols-3 gap-8 items-stretch">
      <div className="lg:col-span-1">
        <Pill>Classes & workshops</Pill>
        <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-medium text-ink-900 leading-[1.1]">
          Learn alongside a chaplain who has been there.
        </h2>
        <p className="mt-5 text-ink-700 leading-relaxed">
          Live and on-demand classes for individuals, ministries, and workplaces — covering
          grief care, chaplaincy training, caregiver support, and spiritual resilience.
        </p>
      </div>
      <div className="lg:col-span-2 grid sm:grid-cols-2 gap-6">
        {[
          {
            icon: <GraduationCap size={26} weight="duotone" className="text-primary" />,
            title: "Live online classes",
            blurb: "Small-group sessions with practical takeaways, prayer, and discussion.",
          },
          {
            icon: <ChalkboardTeacher size={26} weight="duotone" className="text-primary" />,
            title: "Workshops & trainings",
            blurb: "Equip your team, ministry, or workplace with grief-aware and chaplain-rooted care.",
          },
          {
            icon: <Heart size={26} weight="duotone" className="text-secondary" />,
            title: "Self-paced resources",
            blurb: "Workbook companions to the books — gentle pacing, deep reflection.",
          },
          {
            icon: <Microphone size={26} weight="duotone" className="text-secondary" />,
            title: "Speaking invitations",
            blurb: "Conferences, retreats, healthcare in-services, and ministry gatherings.",
          },
        ].map((c) => (
          <div
            key={c.title}
            className="bg-white rounded-2xl border border-ink-100 p-6 shadow-[0_10px_30px_-15px_rgba(15,23,42,0.10)]"
          >
            <div className="mb-4">{c.icon}</div>
            <div className="font-heading text-lg text-ink-900">{c.title}</div>
            <p className="mt-2 text-sm text-ink-700 leading-relaxed">{c.blurb}</p>
          </div>
        ))}
        <div className="sm:col-span-2 flex flex-wrap gap-3 pt-2">
          <Link
            to="/classes"
            data-testid="acd-classes-cta"
            className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-6 py-3 font-semibold text-sm hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
          >
            Browse upcoming classes <ArrowUpRight size={14} weight="bold" />
          </Link>
          <a
            href="#speaking"
            className="inline-flex items-center gap-2 border-2 border-primary text-primary rounded-full px-6 py-3 font-semibold text-sm hover:bg-primary/5 transition-all duration-300"
          >
            Invite Chaplain Derrick to speak
          </a>
        </div>
      </div>
    </div>
  </section>
);

const SubmitQuestionSection = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) {
      toast.error("Please write your question before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_BASE}/contact`,
        {
          name: form.name || "Anonymous",
          email: form.email || "anonymous@askchaplainderrick.local",
          subject: "[ASK CHAPLAIN DERRICK] Submitted Question",
          message: form.message,
        },
        { withCredentials: false }
      );
      toast.success("Your question has been received. Thank you for trusting us with it.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("We couldn't send your question right now. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="submit-question" className="py-20 sm:py-28">
      <div className="container-x grid lg:grid-cols-[1fr,1.2fr] gap-12 lg:gap-20 items-start">
        <div>
          <Pill accent="secondary">Submit a question</Pill>
          <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-medium text-ink-900 leading-[1.1]">
            Ask the question you've been quietly carrying.
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed">
            Send in any question — about grief, faith, caregiving, doubt, or a hard season
            you're walking through. Questions may be answered in an upcoming podcast episode
            or class. Your name and contact info will never be shared without permission.
          </p>
          <blockquote className="mt-8 flex items-start gap-3 text-ink-700/90 italic font-heading text-base sm:text-lg border-l-2 border-secondary pl-4">
            <Quotes size={18} weight="duotone" className="text-secondary mt-1 shrink-0" />
            <span>
              "There are no silly questions when life feels heavy. There are only questions
              waiting to be honored with a thoughtful answer."
            </span>
          </blockquote>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white rounded-2xl border border-ink-100 p-6 sm:p-8 shadow-[0_15px_50px_-20px_rgba(15,23,42,0.15)] space-y-5"
          data-testid="acd-question-form"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="acd-name" className="text-xs uppercase tracking-[0.15em] text-ink-700 font-semibold">
                First name <span className="text-ink-500 normal-case tracking-normal">(optional)</span>
              </label>
              <input
                id="acd-name"
                data-testid="acd-question-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-2 w-full rounded-xl border border-ink-100 bg-white px-4 py-3 text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition"
                placeholder="Or leave blank to stay anonymous"
              />
            </div>
            <div>
              <label htmlFor="acd-email" className="text-xs uppercase tracking-[0.15em] text-ink-700 font-semibold">
                Email <span className="text-ink-500 normal-case tracking-normal">(optional)</span>
              </label>
              <input
                id="acd-email"
                type="email"
                data-testid="acd-question-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2 w-full rounded-xl border border-ink-100 bg-white px-4 py-3 text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition"
                placeholder="you@example.com"
              />
            </div>
          </div>
          <div>
            <label htmlFor="acd-message" className="text-xs uppercase tracking-[0.15em] text-ink-700 font-semibold">
              Your question
            </label>
            <textarea
              id="acd-message"
              data-testid="acd-question-message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={6}
              required
              className="mt-2 w-full rounded-xl border border-ink-100 bg-white px-4 py-3 text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition resize-none"
              placeholder="Type your question here. Take your time — there's no rush."
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="acd-question-submit"
            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto bg-primary text-white rounded-full px-7 py-3 font-semibold text-sm hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 disabled:opacity-60 disabled:hover:translate-y-0"
          >
            <PaperPlaneTilt size={16} weight="duotone" />
            {submitting ? "Sending…" : "Submit your question"}
          </button>
        </form>
      </div>
    </section>
  );
};

const EncouragementSignupSection = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email address.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/newsletter`, { email }, { withCredentials: false });
      toast.success("You're in — gentle encouragement is on the way.");
      setEmail("");
    } catch {
      toast.error("We couldn't subscribe you right now. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-20 sm:py-28 bg-softblue/40">
      <div className="container-x grid lg:grid-cols-[1.2fr,1fr] gap-10 items-center">
        <div>
          <Pill>Free encouragement</Pill>
          <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-medium text-ink-900 leading-[1.1]">
            A little hope in your inbox — when you need it.
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed max-w-xl">
            Short, gentle notes of encouragement, Scripture, and practical hope — never spammy,
            never daily, always honest. Unsubscribe any time with one click.
          </p>
        </div>
        <form
          onSubmit={onSubmit}
          className="bg-white rounded-2xl border border-ink-100 p-6 sm:p-7 shadow-[0_15px_50px_-20px_rgba(15,23,42,0.12)]"
          data-testid="acd-encouragement-form"
        >
          <label htmlFor="acd-encouragement-email" className="text-xs uppercase tracking-[0.18em] text-primary font-semibold">
            Subscribe
          </label>
          <div className="mt-3 flex flex-col sm:flex-row gap-3">
            <input
              id="acd-encouragement-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="acd-encouragement-email"
              className="flex-1 rounded-full border border-ink-100 bg-white px-5 py-3 text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition"
              placeholder="you@example.com"
            />
            <button
              type="submit"
              disabled={submitting}
              data-testid="acd-encouragement-submit"
              className="inline-flex items-center justify-center gap-2 bg-secondary text-ink-900 rounded-full px-6 py-3 font-semibold text-sm hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Send me hope"}
            </button>
          </div>
          <p className="mt-3 text-xs text-ink-500">
            By subscribing you agree to receive occasional encouragement emails. We never share
            your address.
          </p>
        </form>
      </div>
    </section>
  );
};

const SpeakingSection = () => {
  const [form, setForm] = useState({ name: "", email: "", organization: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please share your name, email, and a brief note.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_BASE}/contact`,
        {
          name: form.name,
          email: form.email,
          subject: "[SPEAKING REQUEST] Invite Chaplain Derrick",
          message: `Organization: ${form.organization || "Not provided"}\n\n${form.message}`,
        },
        { withCredentials: false }
      );
      toast.success("Thank you — your speaking request has been received.");
      setForm({ name: "", email: "", organization: "", message: "" });
    } catch {
      toast.error("We couldn't send your request right now. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="speaking" className="py-20 sm:py-28">
      <div className="container-x grid lg:grid-cols-[1fr,1.2fr] gap-12 lg:gap-20 items-start">
        <div>
          <Pill accent="secondary">Speaking</Pill>
          <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-medium text-ink-900 leading-[1.1]">
            Invite Chaplain Derrick to speak at your next gathering.
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed">
            Conferences, ministry retreats, healthcare in-services, workplace wellness events,
            chaplaincy programs, grief workshops, and small groups. Topics include grief care,
            caregiver resilience, workplace chaplaincy, end-of-life support, and faith in
            life's hardest seasons.
          </p>
        </div>
        <form
          onSubmit={onSubmit}
          className="bg-white rounded-2xl border border-ink-100 p-6 sm:p-8 shadow-[0_15px_50px_-20px_rgba(15,23,42,0.15)] space-y-5"
          data-testid="acd-speaking-form"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="acd-sp-name" className="text-xs uppercase tracking-[0.15em] text-ink-700 font-semibold">Your name</label>
              <input
                id="acd-sp-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="acd-speaking-name"
                className="mt-2 w-full rounded-xl border border-ink-100 bg-white px-4 py-3 text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition"
              />
            </div>
            <div>
              <label htmlFor="acd-sp-email" className="text-xs uppercase tracking-[0.15em] text-ink-700 font-semibold">Email</label>
              <input
                id="acd-sp-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="acd-speaking-email"
                className="mt-2 w-full rounded-xl border border-ink-100 bg-white px-4 py-3 text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition"
              />
            </div>
          </div>
          <div>
            <label htmlFor="acd-sp-org" className="text-xs uppercase tracking-[0.15em] text-ink-700 font-semibold">
              Organization or church <span className="text-ink-500 normal-case tracking-normal">(optional)</span>
            </label>
            <input
              id="acd-sp-org"
              value={form.organization}
              onChange={(e) => setForm({ ...form, organization: e.target.value })}
              data-testid="acd-speaking-org"
              className="mt-2 w-full rounded-xl border border-ink-100 bg-white px-4 py-3 text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition"
            />
          </div>
          <div>
            <label htmlFor="acd-sp-msg" className="text-xs uppercase tracking-[0.15em] text-ink-700 font-semibold">
              About your event
            </label>
            <textarea
              id="acd-sp-msg"
              required
              rows={5}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="acd-speaking-message"
              className="mt-2 w-full rounded-xl border border-ink-100 bg-white px-4 py-3 text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition resize-none"
              placeholder="Tell us about your audience, dates, and what you're hoping to explore."
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="acd-speaking-submit"
            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto bg-primary text-white rounded-full px-7 py-3 font-semibold text-sm hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 disabled:opacity-60"
          >
            <PaperPlaneTilt size={16} weight="duotone" />
            {submitting ? "Sending…" : "Send speaking inquiry"}
          </button>
        </form>
      </div>
    </section>
  );
};

const ContactSection = () => (
  <section id="contact" className="py-20 sm:py-28 bg-softblue/30">
    <div className="container-x grid sm:grid-cols-2 gap-8">
      <a
        href={`mailto:${SITE.email}`}
        data-testid="acd-contact-email"
        className="bg-white rounded-2xl border border-ink-100 p-7 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 flex items-start gap-4"
      >
        <EnvelopeSimple size={28} weight="duotone" className="text-primary mt-0.5 shrink-0" />
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-primary font-semibold">Email</div>
          <div className="mt-2 font-heading text-xl text-ink-900 break-all">{SITE.email}</div>
          <div className="mt-1 text-sm text-ink-700">For general questions and ministry inquiries.</div>
        </div>
      </a>
      <div
        data-testid="acd-contact-address"
        className="bg-white rounded-2xl border border-ink-100 p-7 flex items-start gap-4"
      >
        <MapPin size={28} weight="duotone" className="text-primary mt-0.5 shrink-0" />
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-primary font-semibold">Mail</div>
          <address className="mt-2 not-italic font-heading text-xl text-ink-900 leading-snug">
            {SITE.name}
            <br />
            {SITE.address.line1}
            <br />
            {SITE.address.line2}
          </address>
        </div>
      </div>
    </div>
  </section>
);

const Disclaimer = () => (
  <section className="py-12 sm:py-16 border-t border-ink-100">
    <div className="container-x">
      <div
        data-testid="acd-disclaimer"
        className="max-w-4xl mx-auto bg-white rounded-2xl border border-ink-100 p-6 sm:p-8 flex items-start gap-4"
      >
        <Info size={22} weight="duotone" className="text-primary mt-1 shrink-0" />
        <p className="text-sm text-ink-700 leading-relaxed">
          Ask Chaplain Derrick provides faith-based encouragement, spiritual reflection,
          education, and practical support. It is not a substitute for licensed medical, mental
          health, legal, financial, or emergency crisis care. If you are in crisis, call{" "}
          <a
            href="tel:988"
            className="text-primary font-semibold underline-offset-2 hover:underline"
          >
            988
          </a>{" "}
          or seek immediate professional help.
        </p>
      </div>
    </div>
  </section>
);

// ---------- Page ----------

const AskChaplainDerrick = () => {
  return (
    <div data-testid="page-ask-chaplain-derrick">
      <Seo
        title="Ask Chaplain Derrick — Faith-Based Answers"
        description="Submit a heartfelt question for Chaplain Derrick Hall. Get compassionate, faith-based reflection on grief, caregiving, hospice, faith, and hard questions."
      />
      <HeroSection />
      <AboutSection />
      <PodcastSection />
      <FeaturedBookSection />
      <ClassesSection />
      <SubmitQuestionSection />
      <EncouragementSignupSection />
      <SpeakingSection />
      <ContactSection />
      <Disclaimer />
    </div>
  );
};

export default AskChaplainDerrick;
